<template>
    <div
        :class="{'opacity-50 pointer-events-none': disabled}"
        class="redesign-layout-selector relative flex flex-col"
    >
        <div class="flex items-center gap-4 relative">
            <RedesignLayoutSelect
                ref="layoutSelectRef"
                v-model="localModel"
                @on-change-layout="onChangeLayout"
            />
            <div
                v-if="selectedLayout"
                class="flex items-center gap-2"
            >
                <RedesignBaseIconButton
                    v-if="selectedLayout.LayoutAccountID"
                    :size="20"
                    icon="vc-icon-edit-pencil"
                    color="secondary"
                    @click="onEditLayout"
                />
                <RedesignBaseIconButton
                    :size="20"
                    icon="vc-icon-copy"
                    color="secondary"
                    @click="onCopyLayout"
                />
                <RedesignBaseIconButton
                    v-if="selectedLayout.LayoutAccountID"
                    :size="20"
                    icon="vc-icon-recycle-bin"
                    color="secondary"
                    @click="onRemoveLayout"
                />
            </div>
        </div>
        <div
            v-if="addAction"
            class="mt-3 lg:mt-4"
        >
            <RedesignBaseButton
                data-testid="layout-new-button"
                size="small"
                type="outline"
                color="primary"
                icon="vc-icon-add"
                @click="onCreateLayout"
            >
                {{ $t('layout.new.newLayout') }}
            </RedesignBaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { ref } from 'vue'
import { ILayoutItem } from '@/types/dashboard'
import RedesignBaseIconButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseIconButton.vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignLayoutSelect from '@/components/RedesignComponents/Select/RedesignLayoutSelect.vue'
import { defaultLayout } from '@/enum/default-layout-config'
import { selectedAccountID } from '@/composables/globalData/useEntitiesData'

const props = defineProps<{
    addAction?: boolean
    value?: number
    disabled?: boolean
}>()
const emit = defineEmits<{
    (e: 'input', value?: number): void
    (e: 'update:value', value?: number): void
    (e: 'on-change-layout', layoutData: ILayoutItem): void
    (e: 'on-edit-layout', layoutData: ILayoutItem): void
    (e: 'on-copy-layout', layoutData: ILayoutItem): void
    (e: 'on-remove-layout', layoutData: ILayoutItem): void

}>()

/* Data */
const localModel = useVModel(props, 'value', emit, { eventName: 'input' })
const selectedLayout = ref<ILayoutItem | undefined>()
const layoutSelectRef = ref<typeof RedesignLayoutSelect>()

/* Methods */
function onChangeLayout (layoutData?: ILayoutItem) {
    selectedLayout.value = layoutData ? { ...layoutData } : undefined
    emit('on-change-layout', selectedLayout.value )
}
function getDefaultLayout () {
    const layout = getLayoutById(1) || defaultLayout()
    return {
        ...layout,
        LayoutID: undefined,
        LayoutName: 'New Layout',
        LayoutAccountID: selectedAccountID.value
    }
}
function onCreateLayout () {
    emit('on-edit-layout', getDefaultLayout())
}
function onEditLayout () {
    emit('on-edit-layout', selectedLayout.value)
}
function onCopyLayout () {
    emit('on-copy-layout', selectedLayout.value)
}

function onRemoveLayout () {
    emit('on-remove-layout', selectedLayout.value)
}
function onRefreshData (cb?: () => void) {
    layoutSelectRef.value?.refresh(cb)
}
function getLayoutById (id: number) {
    return layoutSelectRef.value?.getLayoutById(id)
}

defineExpose({
    refreshData: onRefreshData,
    onRemoveLayout,
    getLayoutById
})
</script>
